import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Alert,
    AlertTitle,
    Autocomplete,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
} from "@mui/material";
import moment from "moment";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import RichTextEditor from "components/Shared/RichTextEditor";
import FileDropzone from "components/Shared/FileDropzone";

import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { useAuth, useDOUploadFile } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { formHookInputHelper } from "utils/formHookField";

import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";
import { useLazyGetDdlVendorProfileForVendorTicketQuery } from "store/services/vendorProfileApi";

const ALLOWED_TICKET_TYPE_NAMES = ["COD Request", "Pickup Request", "General"];

const INITIAL_VALUE = {
    entity: "",
    source: "",
    sourceRef: "",
    subject: "",
    stage: "Open",
    description: "",
    responsible: "",
    dueDate: "",
    attachments: [],
    isActive: true,
    ticketType: null,
    bankDetails: {
        bankName: "",
        bankAccountName: "",
        bankAccountNumber: "",
    },
    address: "",
};

const VendorTicketForm = ({ ticket = null, isBusy = false, onSubmit = () => { } }) => {
    const navigate = useNavigate();
    const isInitialMount = useRef(true);

    const ticketStageEnums = ["Open", "In Progress", "Closed"];

    const { viewEntity, isAccountAdmin, isSystemAdmin, isVendor, isVendorStaff } =
        useAuth();

    const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
        setValue,
        getValues,
    } = useForm({
        defaultValues: INITIAL_VALUE,
    });

    const [descriptionKey, setDescriptionKey] = useState(0);
    const stableInitialValue = useRef(ticket?.description || "<p>Enter Ticket Description.</p>");
    const _watchTicketType = useWatch({ control, name: "ticketType" });

    const selectedEntity = watch("entity");
    const _watchAccount = useWatch({ control, name: "account" });
    const _watchSource = useWatch({ control, name: "source" });
    const _watchSourceRef = useWatch({ control, name: "sourceRef" });

    const isCODRequest = _watchTicketType?.label === "COD Request";
    const isPickupRequest = _watchTicketType?.label === "Pickup Request";

    const {
        data: ticketTypes = [],
        isLoading: isLoadingTicketTypes,
        isFetching: isFetchingTicketTypes,
    } = useListListQuery(
        { type: ENTITY_LIST_CONSTANTS.TICKET_TYPE, status: "Active" },
        { skip: false, refetchOnMountOrArgChange: true }
    );

    const { data: accounts, isLoading: loadingAccounts } =
        useGetDDLAccountListQuery();

    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();

    const [
        fetchUsers,
        { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers },
    ] = useLazyGetDDLSystemUserListQuery();

    const [
        fetchVendorProfile,
        { data: vendors, isLoading: loadingVendor, isFetching: fetchingVendor },
    ] = useLazyGetDdlVendorProfileForVendorTicketQuery();

    const loading = isBusy || uploadingFiles || loadingUsers || fetchingUsers;

    const _mappedEntities = useMemo(
        () =>
            entities?.map((entity) => ({
                label: entity?.name,
                id: entity?._id,
            })) ?? [],
        [entities]
    );

    const _mappedTicketTypes = useMemo(
        () =>
            Array.isArray(ticketTypes)
                ? ticketTypes
                    .filter((item) => ALLOWED_TICKET_TYPE_NAMES.includes(item?.name))
                    .map((item) => ({
                        label: item?.name || "",
                        _id: item?._id || "",
                    }))
                : [],
        [ticketTypes]
    );

    const _mappedUsers = useMemo(
        () =>
            userList?.map((el) => ({
                label: el?.name,
                id: el?._id,
            })) || [],
        [userList]
    );

    const sourceInfo = useMemo(() => {
        switch (_watchSource) {
            case "Vendor Profile":
                return { data: vendors || [], isBusy: loadingVendor || fetchingVendor };
            default:
                return { data: [], isBusy: false };
        }
    }, [_watchSource, vendors, loadingVendor, fetchingVendor]);

    const isEditModeWithVendorProfile = ticket && ticket.source === "VendorProfile";

    const handleSubmitTicket = async (data) => {
        delete data?.history;
        delete data.responses;

        const _ticketPayload = {
            ...data,
            responsible: data?.responsible?.id,
            source: data?.source?.replace(/\s+/g, ""),
            sourceRef: data?.sourceRef?._id || data?.sourceRef,
            account: data?.entity?._id || viewEntity?.entity?.account?._id,
            entity: data?.entity?.id || viewEntity?.entity?._id,
            ticketType: data?.ticketType?._id,
            // Only include relevant fields based on ticket type
            bankDetails: isCODRequest ? data.bankDetails : undefined,
            address: isPickupRequest ? data.address : undefined,
        };

        const [existingAttachments, newAttachments] = data?.attachments?.reduce(
            ([o, n], el) =>
                el?.publicId || el?.url ? [[...o, el], n] : [o, [...n, el]],
            [[], []]
        );

        if (newAttachments?.length) {
            const { uploadedFiles } = await handleUploadFiles(newAttachments, {
                folderName: "TICKET",
                entity: viewEntity?.entity?._id,
                account: viewEntity?.entity?.account?._id,
            });
            _ticketPayload.attachments = [...existingAttachments, ...uploadedFiles];
        }

        onSubmit(_ticketPayload);
    };

    // Sync description editor when ticket changes
    useEffect(() => {
        if (ticket?.description) {
            stableInitialValue.current = ticket.description;
            setDescriptionKey(prev => prev + 1);
        }
    }, [ticket?._id]);

    useEffect(() => {
        if (!ticket) {
            stableInitialValue.current = "<p>Enter Ticket Description.</p>";
            setDescriptionKey(prev => prev + 1);
        }
    }, [ticket]);

    const vendorBankAccounts = useMemo(() => {
        if (!_watchSourceRef) return [];
        const accounts = [];
        const seen = new Set();

        // Add active / existing from bankAccount array
        if (Array.isArray(_watchSourceRef?.bankAccount)) {
            _watchSourceRef.bankAccount.forEach((b) => {
                const accNo = b.bankAccountNumber != null ? String(b.bankAccountNumber) : "";
                if (b.bankName && !seen.has(accNo)) {
                    seen.add(accNo);
                    accounts.push({
                        bankName: b.bankName,
                        bankAccountName: b.bankAccountName || "",
                        bankAccountNumber: accNo,
                        label: `${b.bankName}${accNo ? ` - ${accNo}` : ""}${b.bankAccountName ? ` (${b.bankAccountName})` : ""}`,
                    });
                }
            });
        }

        // Add vendorProfileBankDetail if not already in list
        if (_watchSourceRef?.vendorProfileBankDetail?.bankName) {
            const vDetail = _watchSourceRef.vendorProfileBankDetail;
            const accNo = vDetail.bankAccountNumber != null ? String(vDetail.bankAccountNumber) : "";
            if (!seen.has(accNo)) {
                seen.add(accNo);
                accounts.push({
                    bankName: vDetail.bankName,
                    bankAccountName: vDetail.bankAccountName || "",
                    bankAccountNumber: accNo,
                    label: `${vDetail.bankName}${accNo ? ` - ${accNo}` : ""}${vDetail.bankAccountName ? ` (${vDetail.bankAccountName})` : ""}`,
                });
            }
        }

        return accounts;
    }, [_watchSourceRef]);

    // Auto-set subject for COD Request on create
    useEffect(() => {
        if (!ticket) {
            if (isCODRequest) {
                setValue("subject", "COD Request");
            } else if (isPickupRequest) {
                setValue("subject", "Pickup Request");
            } else if (_watchTicketType && ["COD Request", "Pickup Request"].includes(getValues("subject"))) {
                setValue("subject", "");
            }
        }
    }, [_watchTicketType, setValue, ticket, getValues, isCODRequest, isPickupRequest]);

    // Auto-populate bank details when COD Request is selected (create mode only)
    useEffect(() => {
        if (!ticket && isCODRequest && vendorBankAccounts.length > 0) {
            const defaultBank = vendorBankAccounts[0];
            setValue("bankDetails.bankName", defaultBank.bankName || "");
            setValue("bankDetails.bankAccountName", defaultBank.bankAccountName || "");
            setValue("bankDetails.bankAccountNumber", defaultBank.bankAccountNumber || "");
        }
    }, [isCODRequest, vendorBankAccounts, setValue, ticket]);

    // Auto-populate address when Pickup Request is selected (create mode only)
    useEffect(() => {
        if (!ticket && isPickupRequest && _watchSourceRef?.address) {
            setValue("address", _watchSourceRef.address);
        }
    }, [isPickupRequest, _watchSourceRef, setValue, ticket]);

    // Initialize form defaults
    useEffect(() => {
        if (isInitialMount.current) {
            const defaultValues = {
                ...INITIAL_VALUE,
                entity: viewEntity?.entity
                    ? { label: viewEntity.entity.name, id: viewEntity.entity._id }
                    : "",
                account: viewEntity?.entity?.account || "",
            };
            reset(defaultValues);
            isInitialMount.current = false;
        }
    }, [reset, viewEntity]);

    // Reset form when editing existing ticket
    useEffect(() => {
        if (!ticket) return;

        const ticketType = ticketTypes.find(
            (type) => type._id === (ticket.ticketType?._id || ticket.ticketType)
        );

        const resetData = {
            ...ticket,
            source: ticket?.source
                ? ticket.source.replace(/([a-z])([A-Z])/g, "$1 $2")
                : "",
            entity: ticket?.entity
                ? { label: ticket.entity.name, id: ticket.entity._id }
                : "",
            responsible: ticket?.responsible
                ? {
                    label: ticket.responsible.designation
                        ? `${ticket.responsible.name} | ${ticket.responsible.designation}`
                        : ticket.responsible.name,
                    id: ticket.responsible._id,
                }
                : "",
            ticketType: ticketType
                ? { _id: ticketType._id, label: ticketType.name }
                : null,
            dueDate: ticket?.dueDate
                ? moment(ticket.dueDate).format("YYYY-MM-DD")
                : "",
            bankDetails: ticket?.bankDetails || {
                bankName: "",
                bankAccountName: "",
                bankAccountNumber: "",
            },
            address: ticket?.address || "",
        };

        reset(resetData);
    }, [ticket, ticketTypes, reset]);

    useEffect(() => {
        const entityId = selectedEntity?.id || viewEntity?.entity?._id;
        if (entityId) fetchUsers({ entity: entityId });
    }, [selectedEntity?.id, viewEntity?.entity?._id, fetchUsers]);

    useEffect(() => {
        if (isAccountAdmin || isSystemAdmin) {
            const accountId = _watchAccount?._id || viewEntity?.entity?.account?._id;
            if (accountId) fetchEntities({ account: accountId });
        }
    }, [_watchAccount?._id, viewEntity?.entity?.account?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

    useEffect(() => {
        if (_watchAccount?._id && selectedEntity?.id) {
            fetchVendorProfile({ entity: selectedEntity.id, account: _watchAccount._id });
        }
    }, [_watchAccount?._id, selectedEntity?.id, fetchVendorProfile]);

    useEffect(() => {
        if (
            (isVendor || isVendorStaff) &&
            viewEntity?.entity?._id &&
            viewEntity?.entity?.account?._id
        ) {
            setValue("source", "Vendor Profile");
            fetchVendorProfile({
                entity: viewEntity.entity._id,
                account: viewEntity.entity.account._id,
            });
        }
    }, [isVendor, isVendorStaff, viewEntity?.entity?._id, viewEntity?.entity?.account?._id, setValue, fetchVendorProfile]);

    useEffect(() => {
        if ((isVendor || isVendorStaff) && vendors?.length > 0 && !watch("sourceRef")) {
            setValue("sourceRef", vendors[0]);
        }
    }, [isVendor, isVendorStaff, vendors, setValue, watch]);

    return (
        <AppForm loading={loading}>
            <AppForm.Content>
                <Grid container spacing={6}>
                    {!isVendor && (
                        <>
                            {(isAccountAdmin || isSystemAdmin) && (
                                <Grid item md={6}>
                                    <Controller
                                        name="account"
                                        control={control}
                                        rules={{ required: "Please select an account." }}
                                        render={({ field: { value, onChange } }) => (
                                            <BaseAutocomplete
                                                fullWidth
                                                label="Account"
                                                value={value || null}
                                                onChange={(_, data) => {
                                                    onChange(data);
                                                    setValue("entity", "");
                                                    setValue("source", "");
                                                }}
                                                getOptionLabel={(option) => option?.name || ""}
                                                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                                options={accounts || []}
                                                loading={loadingAccounts}
                                                loadingText="Loading Accounts..."
                                                disabled={!isSystemAdmin || isEditModeWithVendorProfile}
                                            />
                                        )}
                                    />
                                    {errors.account && (
                                        <FormHelperText error>{errors.account.message}</FormHelperText>
                                    )}
                                </Grid>
                            )}

                            {(isAccountAdmin || isSystemAdmin) && (
                                <Grid item md={6}>
                                    <Controller
                                        name="entity"
                                        control={control}
                                        rules={{ required: "Please select an entity." }}
                                        render={({ field: { value, onChange } }) => (
                                            <BaseAutocomplete
                                                fullWidth
                                                label="Entity"
                                                value={value || null}
                                                onChange={(_, data) => {
                                                    onChange(data);
                                                    setValue("responsible", "");
                                                    setValue("source", "");
                                                }}
                                                getOptionLabel={(option) => option?.label || ""}
                                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                options={_mappedEntities}
                                                loading={loadingEntities}
                                                loadingText="Loading Entities..."
                                                disabled={isEditModeWithVendorProfile}
                                            />
                                        )}
                                    />
                                    {errors.entity && (
                                        <FormHelperText error>{errors.entity.message}</FormHelperText>
                                    )}
                                </Grid>
                            )}
                        </>
                    )}

                    {!isVendor && !isVendorStaff && (
                        <Grid item xs={6}>
                            <Controller
                                name="source"
                                control={control}
                                render={(props) => (
                                    <BaseSelect
                                        {...formHookInputHelper(props)}
                                        fullWidth
                                        label="Source"
                                        getOptionLabel={(opt) => opt}
                                        isOptionEqualToValue={(opt, value) => opt === value}
                                        onChange={(e) => {
                                            props.field.onChange(e);
                                            setValue("sourceRef", "");
                                        }}
                                        options={["Vendor Profile"]}
                                        disabled={isEditModeWithVendorProfile}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    <Grid item md={6}>
                        <Controller
                            name="sourceRef"
                            control={control}
                            render={(props) => (
                                <BaseAutocomplete
                                    {...formHookInputHelper(props)}
                                    fullWidth
                                    label={
                                        isVendor || isVendorStaff
                                            ? "Vendor Profile"
                                            : _watchSource || "Select Source First"
                                    }
                                    onChange={(_, data) => props?.field?.onChange(data)}
                                    getOptionLabel={(option) =>
                                        option ? `${option?.ownerName} - ${option?.shopName}` : ""
                                    }
                                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                    options={sourceInfo.data}
                                    loading={sourceInfo.isBusy}
                                    loadingText="Loading Vendor Profiles..."
                                    disabled={
                                        (!isVendor && !isVendorStaff && !_watchSource) ||
                                        isEditModeWithVendorProfile
                                    }
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={6}>
                        <Controller
                            name="ticketType"
                            control={control}
                            rules={{ required: "Ticket Type is required." }}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    required
                                    label="Ticket Type"
                                    value={value || null}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(option) => option?.label || ""}
                                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                    options={_mappedTicketTypes}
                                    loading={isLoadingTicketTypes || isFetchingTicketTypes}
                                    loadingText="Loading Ticket Types..."
                                    disabled={
                                        isLoadingTicketTypes ||
                                        isFetchingTicketTypes ||
                                        isEditModeWithVendorProfile
                                    }
                                    helperText={errors?.ticketType?.message}
                                    error={!!errors?.ticketType}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Ticket Type"
                                            required
                                            error={!!errors?.ticketType}
                                            helperText={errors?.ticketType?.message}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {(isLoadingTicketTypes || isFetchingTicketTypes) && (
                                                            <CircularProgress color="inherit" size={20} />
                                                        )}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item md={12}>
                        <Controller
                            name="subject"
                            control={control}
                            rules={{ required: "Please provide a subject." }}
                            render={({ field }) => (
                                <TextField
                                    fullWidth
                                    label="Subject"
                                    type="text"
                                    size="small"
                                    required
                                    {...field}
                                    disabled={isEditModeWithVendorProfile}
                                />
                            )}
                        />
                        {errors.subject && (
                            <FormHelperText error>{errors.subject.message}</FormHelperText>
                        )}
                    </Grid>

                    {/* COD Request - Bank Details */}
                    {isCODRequest && (
                        <>
                            <Grid item xs={12}>
                                <Alert severity="warning" variant="outlined">
                                    <AlertTitle><strong>Important Notice: COD Payout Account</strong></AlertTitle>
                                    Your COD funds and money transfers will be sent directly to this bank account. Please verify that the <strong>Bank Name</strong>, <strong>Account Name</strong>, and <strong>Account Number</strong> are completely accurate before submitting.
                                </Alert>
                            </Grid>

                            <Grid item md={4}>
                                <Controller
                                    name="bankDetails.bankName"
                                    control={control}
                                    rules={{ required: "Bank Name is required." }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            freeSolo
                                            fullWidth
                                            size="small"
                                            disabled={isEditModeWithVendorProfile}
                                            options={vendorBankAccounts}
                                            getOptionLabel={(option) => {
                                                if (typeof option === "string") return option;
                                                return option?.bankName || "";
                                            }}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.bankAccountNumber || option.bankName}>
                                                    {option.label}
                                                </li>
                                            )}
                                            value={field.value || ""}
                                            onInputChange={(_, newInputValue) => {
                                                field.onChange(newInputValue);
                                            }}
                                            onChange={(_, newValue) => {
                                                if (newValue && typeof newValue === "object") {
                                                    setValue("bankDetails.bankName", newValue.bankName || "");
                                                    setValue("bankDetails.bankAccountName", newValue.bankAccountName || "");
                                                    setValue("bankDetails.bankAccountNumber", newValue.bankAccountNumber || "");
                                                } else if (typeof newValue === "string") {
                                                    setValue("bankDetails.bankName", newValue);
                                                } else {
                                                    setValue("bankDetails.bankName", "");
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Bank Name"
                                                    size="small"
                                                    required
                                                    error={!!errors?.bankDetails?.bankName}
                                                    helperText={errors?.bankDetails?.bankName?.message}
                                                    placeholder={
                                                        vendorBankAccounts.length > 0
                                                            ? "Select or enter bank name"
                                                            : "Enter bank name"
                                                    }
                                                />
                                            )}
                                        />
                                    )}
                                />
                                {errors?.bankDetails?.bankName && (
                                    <FormHelperText error>
                                        {errors.bankDetails.bankName.message}
                                    </FormHelperText>
                                )}
                            </Grid>

                            <Grid item md={4}>
                                <Controller
                                    name="bankDetails.bankAccountName"
                                    control={control}
                                    rules={{ required: "Account Name is required." }}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label="Account Name"
                                            size="small"
                                            required
                                            {...field}
                                        />
                                    )}
                                />
                                {errors?.bankDetails?.bankAccountName && (
                                    <FormHelperText error>
                                        {errors.bankDetails.bankAccountName.message}
                                    </FormHelperText>
                                )}
                            </Grid>

                            <Grid item md={4}>
                                <Controller
                                    name="bankDetails.bankAccountNumber"
                                    control={control}
                                    rules={{ required: "Account Number is required." }}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label="Account Number"
                                            size="small"
                                            required
                                            {...field}
                                        />
                                    )}
                                />
                                {errors?.bankDetails?.bankAccountNumber && (
                                    <FormHelperText error>
                                        {errors.bankDetails.bankAccountNumber.message}
                                    </FormHelperText>
                                )}
                            </Grid>
                        </>
                    )}

                    {/* Pickup Request - Address */}
                    {isPickupRequest && (
                        <Grid item md={12}>
                            <Controller
                                name="address"
                                control={control}
                                rules={{ required: "Address is required." }}
                                render={({ field }) => (
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        size="small"
                                        required
                                        multiline
                                        rows={2}
                                        {...field}
                                    />
                                )}
                            />
                            {errors.address && (
                                <FormHelperText error>{errors.address.message}</FormHelperText>
                            )}
                        </Grid>
                    )}

                    {!isVendor && (
                        <>
                            <Grid item md={6}>
                                <Controller
                                    name="stage"
                                    control={control}
                                    rules={{ required: "Please select a stage." }}
                                    render={({ field }) => (
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="ticket-stage">Stage</InputLabel>
                                            <Select
                                                fullWidth
                                                labelId="ticket-stage"
                                                label="Stage"
                                                placeholder="Select stage"
                                                value={field.value || ""}
                                                readOnly={!ticket}
                                                {...field}
                                            >
                                                {ticketStageEnums.map((stage) => (
                                                    <MenuItem key={stage} value={stage}>
                                                        {stage}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                />
                                {errors.stage && (
                                    <FormHelperText error>{errors.stage.message}</FormHelperText>
                                )}
                            </Grid>

                            <Grid item md={6}>
                                <Controller
                                    name="dueDate"
                                    control={control}
                                    rules={{ required: "Please provide a due date." }}
                                    render={({ field }) => (
                                        <TextField
                                            required
                                            fullWidth
                                            label="Due Date"
                                            type="date"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                            {...field}
                                            disabled={isEditModeWithVendorProfile}
                                        />
                                    )}
                                />
                                {errors.dueDate && (
                                    <FormHelperText error>{errors.dueDate.message}</FormHelperText>
                                )}
                            </Grid>

                            <Grid item md={6}>
                                <Controller
                                    name="responsible"
                                    control={control}
                                    rules={{ required: "Please select a Responsible User." }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            required
                                            label="Responsible"
                                            loading={loadingUsers || fetchingUsers}
                                            loadingText="Loading Responsible Users..."
                                            options={_mappedUsers}
                                            value={field.value || null}
                                            onChange={(_, data) => field.onChange(data)}
                                            getOptionLabel={(option) => option?.label || ""}
                                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                            error={!!errors?.responsible}
                                            helperText={errors?.responsible?.message}
                                        />
                                    )}
                                />
                            </Grid>

                            {ticket && (
                                <Grid item md={6}>
                                    <Controller
                                        name="isActive"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={<Switch checked={field.value || false} {...field} />}
                                                label="Is Active?"
                                                disabled={isEditModeWithVendorProfile}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid item md={12}>
                        <FormHelperText>Description*</FormHelperText>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <RichTextEditor
                                    key={descriptionKey}
                                    initialValue={stableInitialValue.current}
                                    onChange={onChange}
                                    height={200}
                                    disabled={isEditModeWithVendorProfile}
                                />
                            )}
                        />
                        {errors.description && (
                            <FormHelperText error>{errors.description.message}</FormHelperText>
                        )}
                    </Grid>

                    <Grid item md={12}>
                        <FormHelperText>Attachments</FormHelperText>
                        <Controller
                            name="attachments"
                            control={control}
                            render={({ field }) => (
                                <FileDropzone
                                    multiple
                                    maxFiles={5}
                                    files={field.value || []}
                                    onFileChange={(files) => field.onChange(files)}
                                    disabled={isEditModeWithVendorProfile}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </AppForm.Content>

            <AppForm.Actions>
                <span>
                    <Button disabled={isBusy} onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                </span>
                <span>
                    <Button
                        variant="contained"
                        disabled={isBusy}
                        onClick={handleSubmit(handleSubmitTicket)}
                    >
                        {ticket ? "Update" : "Add"}
                    </Button>
                </span>
            </AppForm.Actions>
        </AppForm>
    );
};

export default VendorTicketForm;